import React, { useEffect, useState } from 'react';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { useStowageStore } from 'src/context/StowageContext';
import TankVisual from './TankVisual';
import { useRotationStore } from 'src/context/RotationContext';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { getFormattedCargoNumber, isManualCargo } from 'src/utils/stowage/cargoOperationUtils';
import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';
import { TankContent } from 'src/types/ViewModel/Stowage/Manifest/TankContent';
import { getTankContentInBerthCall, updateManifest } from 'src/utils/stowage/manifest/cargoManifestUtils';
import { getCargoContentForCondition, getTankContentForCondition } from 'src/utils/stowage/manifest/tankPlanConditionUtils';
import { TankPlanCondition } from 'src/enums/stowage/tankPlanCondition';
import { roundTo3Decimals } from 'src/utils/numberHelper';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import { getHardRuleViolationsInTank, getSoftRuleViolationsInTank } from 'src/utils/stowage/violations/violationsUtils';
import { useViolationsStore } from 'src/context/ViolationsContext';
import { getBerthCallForSelection, getLastBerthCallInPortCall } from 'src/utils/stowage/stowageRotationUtils';
import { Violation } from 'src/types/ViewModel/Stowage/Violation/Violation';
import { calculateVolume } from 'src/utils/stowage/stowageOperation/volumeUtils';
import { getCargoTemperature } from 'src/utils/stowage/stowageOperation/cargoTemperatureUtils';
import { isTankBlocked } from 'src/utils/stowage/stowageOperation/blockedTankUtils';
import Decimal from 'decimal.js';
import { isTankAllowedWithSelectedTankPlanFilter } from 'src/utils/stowage/tankPlanFilterUtils';
import { TankVisualProps } from 'src/types/ViewModel/Vessel/TankVisualProps';
import { undoStowageForCargoAndTank } from 'src/utils/stowage/stowageOperation/undoStowageUtils';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { notStowedWeightInTanks } from 'src/utils/stowage/stowageOperation/stowageOperationUtils';
import { canFillTanks, fillTanks } from 'src/utils/stowage/stowageOperation/fillTankUtils';
import { updateUnstowOperationsForCargo } from 'src/utils/stowage/stowageOperation/autoUnstowUtils';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { useVesselStore } from 'src/context/VesselContext';
import { transferToTank } from 'src/utils/stowage/stowageOperation/transferUtils';
import { hasPermission } from 'src/utils/stowage/permissions/stowagePermissionsUtils';
import { stowageFeatureEnum } from 'src/enums/stowage/stowageFeatureEnum';
import { useAuthStore } from 'src/context/AuthContext';
import { getCargoContentWeight } from 'src/utils/stowage/stowageOperation/cargoContentUtils';

type Props = {
    tank: Tank;
    onZoomClick?: (event: React.MouseEvent) => void; // Changed from HTMLButtonElement to a more generic MouseEvent
    isZoomed?: boolean;
    tankPlanCondition: TankPlanCondition;
    isReport?: boolean;
    filterTanksForReport?: boolean;
};

const TankContainer: React.FC<Props> = ({
    tank,
    onZoomClick,
    isZoomed = false,
    tankPlanCondition,
    isReport = false,
    filterTanksForReport = false,
}) => {
    const {
        setTankContextMenuState,
        isCargoDetailsPanelOpen,
        setIsCargoDetailsPanelOpen,
        viewCargoOperation,
        manifest,
        selectedTankPlanFilters,
        unavailableTanks,
        activeStowagePlan,
        selectedTankPlanCondition,
    } = useStowageStore();
    const { setSelectedCargoOperation, selectedTanks, setSelectedTanks, setCargoDetailsInfo } = useStowageSelectionStore();
    const { selectedStowageRotationBerthCall, selectedStowageRotationPortCall, stowageRotationTree } = useRotationStore();
    const [isSelected, setIsSelected] = useState<boolean>(false);
    const [cofferdams, setCofferdams] = useState<string[]>([]);
    const [cargoContents, setCargoContents] = useState<CargoContent[]>([]);
    const [cargoes, setCargoes] = useState<RotationCargo[] | null>(null);
    const [cargoOperationTypes, setCargoOperationTypes] = useState<string[] | null>(null);
    const [softRuleViolations, setSoftRuleViolations] = useState<Violation[]>([]);
    const [hardRuleViolations, setHardRuleViolations] = useState<Violation[]>([]);
    const [fillWeight, setFillWeight] = useState<number | null>(null);
    const [fillVolume, setFillVolume] = useState<number | null>(null);
    const [overrideFillRatio, setOverrideFillRatio] = useState<boolean | null>(null);
    const [isDimmedDueToCargoOperationSelection, setIsDimmedDueToCargoOperationSelection] = useState<boolean | null>(null);
    const [isDimmedDueToTankPlanCondition, setIsDimmedDueToTankPlanCondition] = useState<boolean | null>(null);
    const [isDimmedDueToSelectedTankPlanFilter, setIsDimmedDueToSelectedTankPlanFilter] = useState<boolean | null>(null);
    const [isDimmedDueToDragAction, setIsDimmedDueToDragAction] = useState<boolean | null>(null);
    const [isBlocked, setIsBlocked] = useState<boolean | null>(null);
    const [isShipFigureAdjusted, setIsShipFigureAdjusted] = useState<boolean | null>(null);
    const { violations } = useViolationsStore();
    const { saveStowageData } = usePostStowageApi();
    const { selectedVessel } = useVesselStore();
    const { role } = useAuthStore();

    useEffect(() => {
        if (!selectedTanks) {
            return;
        }

        const isSelected = selectedTanks?.findIndex((t) => t?.tankId === tank?.tankId) >= 0 ? true : false;
        setIsSelected(isSelected);
    }, [selectedTanks]);

    useEffect(() => {
        const _cofferdams = [];
        if (tank.hasCofferdamPort) _cofferdams.push('top');
        if (tank.hasCofferdamAft) _cofferdams.push('left');
        if (tank.hasCofferdamStarboard) _cofferdams.push('bottom');
        if (tank.hasCofferdamForward) _cofferdams.push('right');
        setCofferdams(_cofferdams);
    }, [tank]);

    useEffect(() => {
        if (!activeStowagePlan || !selectedStowageRotationPortCall || !tankPlanCondition || !manifest) {
            return;
        }

        const tankConditionTankContent = getTankContent(tankPlanCondition);

        if (tankConditionTankContent?.cargoContents && tankConditionTankContent.cargoContents.length > 0) {
            const newCargoes = getCargoes(tankConditionTankContent.cargoContents);
            setCargoes(newCargoes);
            setCargoContents(tankConditionTankContent.cargoContents);

            let _fillWeightNumber = getCargoContentWeight(tankConditionTankContent.cargoContents);

            if (
                tankConditionTankContent.cargoContents[0].stowage.isShipFigureAdjusted &&
                tankConditionTankContent.cargoContents[0].stowage.shipFigure !== null
            ) {
                _fillWeightNumber = new Decimal(tankConditionTankContent.cargoContents[0].stowage.shipFigure);
                setIsShipFigureAdjusted(true);
            } else {
                setIsShipFigureAdjusted(false);
            }

            const _fillVolume = roundTo3Decimals(
                calculateVolume(
                    _fillWeightNumber,
                    tankConditionTankContent.cargoContents[0].cargo,
                    getCargoTemperature(activeStowagePlan, tankConditionTankContent.cargoContents[0].cargo, tank)
                ).toNumber()
            );

            setFillVolume(_fillVolume);
            setFillWeight(_fillWeightNumber.toNumber());
            setOverrideFillRatio(false);
            setIsDimmedDueToTankPlanCondition(false);
        } else {
            if (
                [
                    TankPlanCondition.LoadingOperations,
                    TankPlanCondition.UnloadingOperations,
                    TankPlanCondition.OperationsInCall,
                    TankPlanCondition.TransitOperations,
                ].includes(tankPlanCondition)
            ) {
                setIsDimmedDueToTankPlanCondition(true);
            } else {
                setIsDimmedDueToTankPlanCondition(false);
            }
            setIsShipFigureAdjusted(false);
            setCargoes(null);
            setCargoContents([]);
            setFillVolume(0);
            setFillWeight(0);
        }

        const _isBlocked = isTankBlocked(
            activeStowagePlan,
            tank,
            selectedStowageRotationPortCall,
            selectedStowageRotationBerthCall,
            stowageRotationTree
        );
        setIsBlocked(_isBlocked);

        setSoftRuleViolations(
            getSoftRuleViolationsInTank(violations, tank.tankId, selectedStowageRotationPortCall.portCallGuid, selectedStowageRotationBerthCall)
        );
        setHardRuleViolations(
            getHardRuleViolationsInTank(violations, tank.tankId, selectedStowageRotationPortCall.portCallGuid, selectedStowageRotationBerthCall)
        );

        const berthCall = selectedStowageRotationBerthCall ?? getLastBerthCallInPortCall(selectedStowageRotationPortCall);
        if (berthCall) {
            setIsDimmedDueToSelectedTankPlanFilter(
                !isTankAllowedWithSelectedTankPlanFilter(activeStowagePlan, tank, berthCall, selectedTankPlanFilters)
            );

            setCargoOperationTypes(getCargoOperationTypes());
        } else {
            setIsDimmedDueToSelectedTankPlanFilter(false);
            setCargoOperationTypes([]);
        }
    }, [
        activeStowagePlan,
        selectedStowageRotationPortCall,
        selectedStowageRotationBerthCall,
        tankPlanCondition,
        manifest,
        violations,
        selectedTankPlanFilters,
    ]);

    useEffect(() => {
        if (!cargoContents || cargoContents.length === 0 || !cargoContents[0].cargo) {
            return;
        }

        if (
            viewCargoOperation &&
            getFormattedCargoNumber(
                viewCargoOperation.cargo.voyageNumber,
                viewCargoOperation.cargo.cargoNumber,
                isManualCargo(viewCargoOperation.cargo)
            ) !==
                getFormattedCargoNumber(
                    cargoContents[0].cargo.voyageNumber,
                    cargoContents[0].cargo.cargoNumber,
                    isManualCargo(viewCargoOperation.cargo)
                )
        ) {
            setIsDimmedDueToCargoOperationSelection(true);
        } else {
            setIsDimmedDueToCargoOperationSelection(false);
        }
    }, [viewCargoOperation, cargoContents]);

    useEffect(() => {
        const isTankUnavailable = unavailableTanks?.filter((t) => t.tankId === tank.tankId).length > 0;
        setIsDimmedDueToDragAction(isTankUnavailable);
    }, [unavailableTanks]);

    const getCargoOperationTypes = (): string[] | null => {
        const cargoOperationTypes = [];
        const cargoOperationTypeFilter =
            tankPlanCondition === TankPlanCondition.LoadingOperations
                ? ['Loading']
                : tankPlanCondition === TankPlanCondition.UnloadingOperations
                ? ['Unloading']
                : ['Loading', 'Unloading', 'Transfer'];

        if (selectedStowageRotationBerthCall) {
            const tankContentInBerthCall = getTankContentInBerthCall(activeStowagePlan, selectedStowageRotationBerthCall, tank);
            const addedCargoOperationTypes = tankContentInBerthCall?.cargoContents
                ?.flatMap((c) => c.cargoOperation?.operationType ?? [])
                .filter(Boolean)
                .filter((o) => cargoOperationTypeFilter.includes(o));
            if (addedCargoOperationTypes && addedCargoOperationTypes.length > 0) {
                cargoOperationTypes.push(...addedCargoOperationTypes);
            }
        } else {
            selectedStowageRotationPortCall.berthCalls.forEach((berthCall) => {
                const tankContentInBerthCall = getTankContentInBerthCall(activeStowagePlan, berthCall, tank);

                const addedCargoOperationTypes = tankContentInBerthCall?.cargoContents
                    ?.flatMap((c) => c.cargoOperation?.operationType ?? [])
                    .filter(Boolean)
                    .filter((o) => cargoOperationTypeFilter.includes(o));
                if (addedCargoOperationTypes) {
                    cargoOperationTypes.push(...addedCargoOperationTypes);
                }
            });
        }
        return cargoOperationTypes;
    };

    const getCargoes = (cargoContents: CargoContent[]): RotationCargo[] | null => {
        const validCargoes = cargoContents
            .filter(
                (content) =>
                    (content.cargo && content.weight.gt(0)) ||
                    (content.cargoOperation?.operationType === 'Unloading' &&
                        [TankPlanCondition.UnloadingOperations, TankPlanCondition.OperationsInCall].includes(tankPlanCondition))
            )
            .map((content) => content.cargo);

        // Remove duplicate cargoes by filtering based on cargo GUID
        const uniqueCargoes = validCargoes.filter((cargo, index, self) => self.findIndex((c) => c.cargoGuid === cargo.cargoGuid) === index);

        return uniqueCargoes.length > 0 ? uniqueCargoes : null;
    };

    const getTankContent = (tankPlanCondition: TankPlanCondition): TankContent => {
        return getTankContentForCondition(
            activeStowagePlan,
            selectedStowageRotationPortCall,
            selectedStowageRotationBerthCall,
            tankPlanCondition,
            tank
        );
    };

    /*
    Requirements:
    - The user should be able to select a tank by clicking on it.
    - The user should be able to select multiple tanks by holding down the Ctrl or Cmd key while clicking on them.
    - The user should be able to deselect a tank by clicking on it again.
    - The user should be able to select a new tank by clicking on an empty area of the stowage plan.
    */
    const onClickTank = (tank: Tank, event: React.MouseEvent<HTMLElement>) => {
        if (isTankBlocked(activeStowagePlan, tank, selectedStowageRotationPortCall, selectedStowageRotationBerthCall, stowageRotationTree)) {
            return;
        }

        const isMultiSelect = event.ctrlKey || event.metaKey;
        const currentTanks = selectedTanks ?? [];
        const tankIndex = currentTanks.findIndex((t) => t.tankId === tank.tankId);
        const isTankSelected = tankIndex >= 0;

        if (isMultiSelect) {
            if (isTankSelected) {
                setSelectedTanks(currentTanks.filter((t) => t.tankId !== tank.tankId));
            } else {
                setSelectedTanks([...currentTanks, tank]);
            }
        } else {
            if (currentTanks.length === 1 && isTankSelected) {
                setSelectedTanks([]);
            } else {
                setSelectedTanks([tank]);
            }
            setSelectedCargoOperation(null);
        }
    };

    const onOpenTankMenu = (event: React.MouseEvent<HTMLButtonElement>, tank: Tank) => {
        event.preventDefault();
        event.stopPropagation();

        let xPos = event.clientX;
        let yPos = event.clientY + 20;

        if (event.clientX + 320 > window.innerWidth) {
            const xDiff = window.innerWidth - 320;
            xPos = xDiff;
        }

        if (event.clientY + 380 > window.innerHeight) {
            const yDiff = window.innerHeight - 380;
            yPos = yDiff;
        }

        setTankContextMenuState({
            visible: true,
            tank: tank,
            cargoContents: cargoContents,
            cargoOperationGuid: crypto.randomUUID(),
            berthCall: null,
            x: xPos,
            y: yPos,
            violations: [...hardRuleViolations, ...softRuleViolations],
        });
    };

    const onOpenCargoInfo = (event: React.MouseEvent<HTMLButtonElement>, tank: Tank) => {
        event.preventDefault();
        event.stopPropagation();

        const selectedCargoContent = getCargoContentForCondition(
            activeStowagePlan,
            selectedStowageRotationPortCall,
            selectedStowageRotationBerthCall,
            selectedTankPlanCondition
        );

        const cargoContentItem =
            selectedCargoContent.length > 0
                ? cargoContents.find(
                      (cc) => cc.stowage.cargoOperation.cargoOperationGuid === selectedCargoContent[0].cargoOperation.cargoOperationGuid
                  ) ?? cargoContents[0]
                : cargoContents[0];

        setCargoDetailsInfo({ cargoContent: cargoContentItem, tank });
        setIsCargoDetailsPanelOpen(!isCargoDetailsPanelOpen);
    };

    const canDrag = (): boolean => {
        if (!activeStowagePlan || !selectedStowageRotationPortCall || !cargoContents || !hasPermission(role, stowageFeatureEnum.EditStowage)) {
            return false;
        }
        const dragOperationType = getDragOperationType();
        return dragOperationType == 'Loading' || dragOperationType == 'Transfer';
    };

    const getDragOperationType = (): string | null => {
        return cargoContents?.length > 0 &&
            cargoContents[0].stowage?.stowageGuid &&
            cargoContents[0].stowage?.cargoOperation?.operationType === 'Loading' &&
            cargoContents[0].stowage?.cargoOperation?.berthCall?.portCall?.portCallGuid === selectedStowageRotationPortCall?.portCallGuid
            ? 'Loading'
            : cargoContents?.length > 0 && cargoContents[0].weightInTransit.gt(0)
            ? 'Transfer'
            : null;
    };

    const canDrop = (): boolean => {
        return true;
    };

    const onDropStowageInTank = (stowageGuid: string): StowagePlanChange[] | null => {
        const stowageItem = activeStowagePlan.stowageItems.find((s) => s.stowageGuid === stowageGuid);
        if (stowageItem) {
            if (stowageItem.tankId !== tank.tankId) {
                // TODO: LOGIC-ERROR - There is a bug here. When popup is opened and canceled the stowage operation does not revert back.
                const undoChanges = undoStowageForCargoAndTank(activeStowagePlan, stowageItem.cargoOperation.cargo, stowageItem.tank);

                setSelectedCargoOperation(stowageItem.cargoOperation);
                const openEditStowage = fillTankOrTanks(tank, stowageItem.cargoOperation, undoChanges); // TODO: LOGIC-ERROR - also delete future related transfer operation (and related stowages) if the cargo is transferred to another tank
                return openEditStowage ? undoChanges : null;
            }
        }
        return null;
    };
    const onDropTransferToTank = (stowageGuid: string): StowagePlanChange[] | null => {
        const stowageItem = activeStowagePlan.stowageItems.find((s) => s.stowageGuid === stowageGuid);
        if (stowageItem) {
            if (stowageItem.tankId !== tank.tankId) {
                console.log('Transferring cargo', stowageItem, ' from tank', stowageItem.tank.tankName, 'to tank', tank.tankName);

                const berthCall = getBerthCallForSelection(activeStowagePlan, selectedStowageRotationPortCall, selectedStowageRotationBerthCall);
                if (!berthCall || berthCall.berthId < 0) {
                    console.error('No berth call selected for the transfer operation');
                    return null;
                }

                const sourceCargoContent = getTankContentInBerthCall(activeStowagePlan, berthCall, stowageItem.tank)?.cargoContents?.[0] ?? null;
                const destinationCargoContent = getTankContentInBerthCall(activeStowagePlan, berthCall, tank)?.cargoContents?.[0] ?? null;

                if (!destinationCargoContent || sourceCargoContent.cargo.cargoGuid === destinationCargoContent.cargo.cargoGuid) {
                    return transferToTank(activeStowagePlan, berthCall, sourceCargoContent, destinationCargoContent, stowageItem.tank, tank);
                } else {
                    console.log(
                        'Cannot transfer cargo',
                        sourceCargoContent.cargo,
                        ' to tank',
                        tank.tankName,
                        'as it already contains a different cargo',
                        destinationCargoContent.cargo
                    );
                }
            }
        }
        return null;
    };

    const fillTankOrTanks = (tank: Tank, cargoOperation: RotationCargoOperation, additionalStowagePlanChanges?: StowagePlanChange[]): boolean => {
        let tanks = selectedTanks?.length > 0 && selectedTanks.includes(tank) ? selectedTanks : [tank];

        // For unloading operations, filter tanks to only include those with matching cargo
        if (cargoOperation.operationType === 'Unloading') {
            const berthCall = getBerthCallForSelection(activeStowagePlan, selectedStowageRotationPortCall, selectedStowageRotationBerthCall);
            tanks = tanks.filter((t) => {
                const tankContent = getTankContentInBerthCall(activeStowagePlan, berthCall, t);
                return tankContent?.cargoContents?.some((c) => c.cargo?.cargoGuid === cargoOperation.cargoGuid);
            });
        }

        if (!activeStowagePlan || !cargoOperation || !canFillTanks(activeStowagePlan, tanks, cargoOperation)) {
            return false;
        }

        if (tanks?.length > 1 && tanks.findIndex((t) => t.tankId === tank.tankId) >= 0) {
            const _remainingStowageWeight = notStowedWeightInTanks(activeStowagePlan, cargoOperation);

            const cargoContentsPerTankAtDeparture = tanks.map((t) => ({
                tank: t,
                cargoContent: getCargoContentInTankAtTimeOfOperation(t, cargoOperation),
            }));

            const { stowagePlanChanges } = fillTanks(activeStowagePlan, cargoContentsPerTankAtDeparture, cargoOperation, _remainingStowageWeight);

            updateManifest(activeStowagePlan, selectedVessel);

            const unstowStowagePlanChanges = updateUnstowOperationsForCargo(activeStowagePlan, cargoOperation);

            saveStowageData(activeStowagePlan, stowagePlanChanges.concat(unstowStowagePlanChanges).concat(additionalStowagePlanChanges ?? []));
        } else {
            setSelectedTanks([tank]);
            return true;
        }
        return false;
    };

    const getCargoContentInTankAtTimeOfOperation = (tank: Tank, cargoOperation: RotationCargoOperation): CargoContent => {
        let _selectedStowageRotationBerthCall = selectedStowageRotationBerthCall;
        if (!_selectedStowageRotationBerthCall && cargoOperation) {
            _selectedStowageRotationBerthCall = cargoOperation.berthCall;
        }

        if (_selectedStowageRotationBerthCall) {
            const tankContent = getTankContentInBerthCall(activeStowagePlan, _selectedStowageRotationBerthCall, tank);

            if (tankContent?.cargoContents && tankContent.cargoContents.length > 0) {
                return tankContent.cargoContents[0];
            }
        }
        return null;
    };

    const tankVisualProps: TankVisualProps = {
        tank: tank,
        canDrag: canDrag,
        getDragOperationType: getDragOperationType,
        canDrop: canDrop,
        onDropStowageInTank: onDropStowageInTank,
        onDropTransferToTank: onDropTransferToTank,
        fillTankOrTanks: fillTankOrTanks,
        onClickTank: onClickTank,
        onOpenTankMenu: onOpenTankMenu,
        onOpenCargoInfo: onOpenCargoInfo,
        onZoomClick: onZoomClick,
        isSelected: isSelected,
        isDimmed:
            !isReport &&
            (isDimmedDueToCargoOperationSelection ||
                isDimmedDueToTankPlanCondition ||
                isDimmedDueToSelectedTankPlanFilter ||
                isDimmedDueToDragAction),
        isBlocked: isBlocked,
        isShipFigureAdjusted: isShipFigureAdjusted,
        isZoomed: isZoomed,
        isReport: isReport,
        cofferDams: cofferdams,
        cargoes: isSelected || !filterTanksForReport ? cargoes : null,
        cargoContents: isSelected || !filterTanksForReport ? cargoContents : null,
        fillWeight: fillWeight,
        fillVolume: fillVolume,
        overrideFillRatio: overrideFillRatio,
        cargoOperationTypes: cargoOperationTypes,
        softRuleViolations: softRuleViolations,
        hardRuleViolations: hardRuleViolations,
    };

    return <TankVisual vm={tankVisualProps} />;
};

export default TankContainer;
